import { Assert } from "../../Assert";
import { Nullable, PartialNullable, RadPromiseLike } from "../../Extensions";
import { PromiseHelper } from "../../Helpers/PromiseHelper";
import { $$, IJNativeEvent, JNative, JNativeStatic } from "../../JNative";
import { PropertyChange } from "../../PropertyChange";
import { Property, PropertyBoolean, PropertyString } from "../../RadObject";
import { ClientAwareCategoryAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareCategoryAttribute";
import { ClientAwareDisplayNameAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareDisplayNameAttribute";
import { DialogResultEnum } from "../Dialogs/DialogResultEnum";
import { IDialogResponse } from "../Dialogs/IDialogResponse";
import { PageManager } from "../PageManager";
import { IPage, Page, PagePropertyValues } from "../Pages/Page";
import { Button } from "./Button";
import { ButtonStyleEnum } from "./ButtonStyleEnum";
import { IPageDimmer, PageDimmer } from "./PageDimmer";
import { NAMESPACE } from "./_Namespace";

import "./Dialog.scss";

export type DialogPropertyValues =
    PagePropertyValues
    & {
        abortButtonText: string;
        animationIn: string;
        animationOut: string;
        blockNavigation: boolean;
        buttonStyle: ButtonStyleEnum;
        cancelButtonText: string;
        fullWidth: boolean;
        ignoreButtonText: string;
        noButtonText: string;
        okButtonText: string;
        retryButtonText: string;
        yesButtonText: string;
    };

export type DialogProperties = {
    [name in keyof DialogPropertyValues]: Property<DialogPropertyValues[name]>
};

export interface IDialog extends IPage, DialogProperties
{
    dialogResult?: DialogResultEnum;
    promiseData?: any;

    cancelAsync(): void;
    closeAsync(): RadPromiseLike<any>;
    openAsync(data?: any): RadPromiseLike<IDialogResponse>;
}

export class Dialog<TPropertyValues extends DialogPropertyValues = DialogPropertyValues> extends Page<TPropertyValues> implements IDialog
{
    // #region Static

    public static override classNamespace = NAMESPACE + "Dialog";
    public static override className = "Dialog";

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<DialogPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                abortButtonText: "Abort",
                animationIn: "slide left",
                animationOut: "slide left",
                blockNavigation: true,
                buttonStyle: ButtonStyleEnum.okOnly,
                cancelButtonText: "Cancel",
                ignoreButtonText: "Ignore",
                noButtonText: "No",
                okButtonText: "OK",
                retryButtonText: "Retry",
                tabStop: true,
                yesButtonText: "Yes",
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    public $$footerElement?: JNative<HTMLElement>;
    protected buttonOk?: Button;
    protected buttonYes?: Button;
    protected buttonNo?: Button;
    protected buttonAbort?: Button;
    protected buttonRetry?: Button;
    protected buttonIgnore?: Button;
    protected buttonCancel?: Button;
    protected dimmer?: IPageDimmer;

    // #endregion

    // #region Properties

    public abortButtonText!: PropertyString;
    public animationIn!: PropertyString;
    public animationOut!: PropertyString;
    public blockNavigation!: PropertyBoolean;
    public buttonStyle!: Property<ButtonStyleEnum>;
    public cancelButtonText!: PropertyString;
    public fullWidth!: PropertyBoolean;
    public ignoreButtonText!: PropertyString;
    public noButtonText!: PropertyString;
    public okButtonText!: PropertyString;
    public retryButtonText!: PropertyString;
    public yesButtonText!: PropertyString;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<TPropertyValues>)
    {
        super(propertyValues);

        this._blockNavigationFunction = () =>
        {
            return this.dimmer?.shown() ?? false;
        };

        this.opened = false;

        this.addCssClass("rad-control-dialog");
        this._containerElementRef.current.addClass("rad-control-container content");
        this.$$footerElement?.addClass("rad-dialog-footer");

        this.buttonOk?.addCssClass("rad-button-ok ui button primary");
        this.buttonYes?.addCssClass("rad-button-yes ui right labeled icon button primary");
        this.buttonNo?.addCssClass("rad-button-no ui button");
        this.buttonAbort?.addCssClass("rad-button-abort ui button negative");
        this.buttonRetry?.addCssClass("rad-button-retry ui button primary");
        this.buttonIgnore?.addCssClass("rad-button-ignore ui button");
        this.buttonCancel?.addCssClass("rad-button-cancel ui button");

        this._containerWrapperElementRef.current.removeClass("container");
        this._containerWrapperElementRef.current.addClass("content ui form");

        this.detach();

        // Trigger Property Changes
        this.abortButtonText.triggerChange();
        this.buttonStyle.triggerChange();
        this.cancelButtonText.triggerChange();
        this.fullWidth.triggerChange();
        this.ignoreButtonText.triggerChange();
        this.noButtonText.triggerChange();
        this.okButtonText.triggerChange();
        this.retryButtonText.triggerChange();
        this.yesButtonText.triggerChange();
    }

    // #endregion

    // #region Public Methods

    public async cancelAsync()
    {
        const
            buttonStyle = this.buttonStyle.get(),
            canCancel = ([
                ButtonStyleEnum.okCancel,
                ButtonStyleEnum.yesNoCancel,
                ButtonStyleEnum.retryCancel,
                ButtonStyleEnum.cancelOnly,
            ] as Nullable<ButtonStyleEnum>[]).indexOf(buttonStyle) >= 0;

        if (!canCancel)
            return;

        this.dialogResult = DialogResultEnum.cancel;
        this.promiseData = null;

        await this.closeAsync();
    }
    public async closeAsync(): Promise<any>
    {
        const
            shouldClose = await this._onClosingAsync();

        if (!shouldClose)
            return;

        if (this.closePromise)
            return this.closePromise;

        this.closePromise = PromiseHelper.createPromisePublic();

        this.removeCssClass("visible");

        await this._getDimmer().hideElementOrControlAsync(this);

        this.opened = false;

        this.openPromise?.resolve({ dialogResult: this.dialogResult ?? DialogResultEnum.none, promiseData: this.promiseData });
        delete this.openPromise;

        this.closePromise?.resolve({ dialogResult: this.dialogResult ?? DialogResultEnum.none, promiseData: this.promiseData });
        delete this.closePromise;

        PageManager.instance?.unRegisterBlockNavigationFunction(this._blockNavigationFunction);

        return this.closePromise;
    }
    public openAsync(data?: any)
    {
        if (this.openPromise)
            return this.openPromise;

        this.opened = true;
        this.openPromise = PromiseHelper.createPromisePublic();

        delete this.dialogResult;
        delete this.promiseData;

        this._getDimmer().showElementOrControlAsync(this)
            .then(() =>
            {
                PageManager.instance?.registerBlockNavigationFunction(this._blockNavigationFunction);
                this.focus();
            });

        return this.openPromise as Promise<IDialogResponse>;
    }

    // #endregion

    // #region Protected Methods

    protected _abortButtonTextChanged(change: PropertyChange<string>)
    {
        this.buttonAbort?.text.set(change.currentValue);
    }
    protected _animationInChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _animationOutChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _blockNavigationChanged(change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }
    protected _buttonStyleChanged(change: PropertyChange<ButtonStyleEnum>)
    {
        this.buttonOk?.hide();
        this.buttonYes?.hide();
        this.buttonNo?.hide();
        this.buttonAbort?.hide();
        this.buttonRetry?.hide();
        this.buttonIgnore?.hide();
        this.buttonCancel?.hide();

        switch (change.currentValue)
        {
            case ButtonStyleEnum.okOnly:
                this.buttonOk?.appendTo(this.$$footerElement);
                this.buttonOk?.show();
                break;
            case ButtonStyleEnum.okCancel:
                this.buttonOk?.appendTo(this.$$footerElement);
                this.buttonOk?.show();
                this.buttonCancel?.appendTo(this.$$footerElement);
                this.buttonCancel?.show();
                break;
            case ButtonStyleEnum.yesNo:
                this.buttonYes?.appendTo(this.$$footerElement);
                this.buttonYes?.show();
                this.buttonNo?.appendTo(this.$$footerElement);
                this.buttonNo?.show();
                break;
            case ButtonStyleEnum.yesNoCancel:
                this.buttonYes?.appendTo(this.$$footerElement);
                this.buttonYes?.show();
                this.buttonNo?.appendTo(this.$$footerElement);
                this.buttonNo?.show();
                this.buttonCancel?.appendTo(this.$$footerElement);
                this.buttonCancel?.show();
                break;
            case ButtonStyleEnum.retryCancel:
                this.buttonRetry?.appendTo(this.$$footerElement);
                this.buttonRetry?.show();
                this.buttonCancel?.appendTo(this.$$footerElement);
                this.buttonCancel?.show();
                break;
            case ButtonStyleEnum.abortRetryIgnore:
                this.buttonAbort?.appendTo(this.$$footerElement);
                this.buttonAbort?.show();
                this.buttonRetry?.appendTo(this.$$footerElement);
                this.buttonRetry?.show();
                this.buttonIgnore?.appendTo(this.$$footerElement);
                this.buttonIgnore?.show();
                break;
            case ButtonStyleEnum.cancelOnly:
                this.buttonCancel?.appendTo(this.$$footerElement);
                this.buttonCancel?.show();
                break;
        }
    }
    protected _cancelButtonTextChanged(change: PropertyChange<string>)
    {
        this.buttonCancel?.text.set(change.currentValue);
    }
    protected _createFooterButton(text: string, cssClass: string): Button | undefined
    {
        const
            button = new Button({ cssClass: cssClass, inline: true, text: text });

        if (this.$$footerElement)
            button.appendTo(this.$$footerElement);

        return button;
    }
    protected _fullWidthChanged(change: PropertyChange<boolean>)
    {
        if (change.currentValue)
            this.addCssClass("rad-state-full-width");
        else
            this.removeCssClass("rad-state-full-width");
    }
    protected _getDimmer()
    {
        if (!this.dimmer)
            this.dimmer = new PageDimmer({ animationIn: this.animationIn.get(), animationOut: this.animationOut.get(), cssClass: "rad-dialog-dimmer" });

        return this.dimmer;
    }
    protected _ignoreButtonTextChanged(change: PropertyChange<string>)
    {
        this.buttonIgnore?.text.set(change.currentValue);
    }
    protected _noButtonTextChanged(change: PropertyChange<string>)
    {
        this.buttonNo?.text.set(change.currentValue);
    }
    protected _okButtonTextChanged(change: PropertyChange<string>)
    {
        this.buttonOk?.text.set(change.currentValue);
    }
    protected async _onClosingAsync(): Promise<boolean>
    {
        return true;
    }
    protected _retryButtonTextChanged(change: PropertyChange<string>)
    {
        this.buttonRetry?.text.set(change.currentValue);
    }
    protected _yesButtonTextChanged(change: PropertyChange<string>)
    {
        this.buttonYes?.text.set(change.currentValue);
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: TPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.abortButtonText = new PropertyString(this, propertyValues.abortButtonText, this._abortButtonTextChanged);
        this.animationIn = new PropertyString(this, propertyValues.animationIn, this._animationInChanged);
        this.animationOut = new PropertyString(this, propertyValues.animationOut, this._animationOutChanged);
        this.blockNavigation = new PropertyBoolean(this, propertyValues.blockNavigation, this._blockNavigationChanged);
        this.buttonStyle = new Property<ButtonStyleEnum>({
            context: this,
            dotNetType: "System.String, System.Private.CoreLib",
            initialValue: propertyValues.buttonStyle,
            changeHandler: this._buttonStyleChanged,
            attributes: [
                new ClientAwareCategoryAttribute({ category: "Appearance" }),
                new ClientAwareDisplayNameAttribute({ displayName: "Button Style" }),
            ],
        });
        this.cancelButtonText = new PropertyString(this, propertyValues.cancelButtonText, this._cancelButtonTextChanged);
        this.fullWidth = new PropertyBoolean(this, propertyValues.fullWidth, this._fullWidthChanged);
        this.ignoreButtonText = new PropertyString(this, propertyValues.ignoreButtonText, this._ignoreButtonTextChanged);
        this.noButtonText = new PropertyString(this, propertyValues.noButtonText, this._noButtonTextChanged);
        this.okButtonText = new PropertyString(this, propertyValues.okButtonText, this._okButtonTextChanged);
        this.retryButtonText = new PropertyString(this, propertyValues.retryButtonText, this._retryButtonTextChanged);
        this.yesButtonText = new PropertyString(this, propertyValues.yesButtonText, this._yesButtonTextChanged);
    }

    protected override _bindEvents()
    {
        super._bindEvents();

        this.on("keyup", { self: this }, this.onKeyUp);
        this.on("click", ">.rad-dialog-footer >.ui.button", { self: this }, this.onFooterButtonClicked);
    }
    protected override _createElement()
    {
        const
            $$element = super._createElement();

        Assert.isNotNullOrUndefined(this._containerWrapperElementRef.current, "this._containerWrapperElementRef.current");

        this._headerElementRef.current.prependTo($$element);

        this.$$footerElement = JNativeStatic.create$$({ nodeName: "div" });
        this.$$footerElement.insertAfter(this._containerWrapperElementRef.current);

        if (this._pageFooterControlRef.current)
            this._pageFooterControlRef.current.remove();

        // Buttons
        this.buttonOk = this._createFooterButton("OK", "rad-button-ok");
        this.buttonYes = this._createFooterButton("Yes", "rad-button-yes");
        this.buttonNo = this._createFooterButton("No", "rad-button-no");
        this.buttonAbort = this._createFooterButton("Abort", "rad-button-abort");
        this.buttonRetry = this._createFooterButton("Retry", "rad-button-retry");
        this.buttonIgnore = this._createFooterButton("Ignore", "rad-button-ignore");
        this.buttonCancel = this._createFooterButton("Cancel", "rad-button-cancel");

        return $$element;
    }
    protected override _dispose()
    {
        this.buttonOk?.remove();
        this.buttonYes?.remove();
        this.buttonNo?.remove();
        this.buttonAbort?.remove();
        this.buttonRetry?.remove();
        this.buttonIgnore?.remove();
        this.buttonCancel?.remove();
        this.dimmer?.remove();

        super._dispose();
    }

    // #endregion

    // #region Event Handlers

    protected async onFooterButtonClicked(evt: IJNativeEvent)
    {
        evt.preventDefault();
        evt.stopPropagation();

        const
            self = evt.data.self as this,
            $$target = $$(evt.currentTarget);

        let
            dialogResult;

        if ($$target.hasClass("rad-button-cancel"))
            dialogResult = DialogResultEnum.cancel;
        else if ($$target.hasClass("rad-button-ignore"))
            dialogResult = DialogResultEnum.ignore;
        else if ($$target.hasClass("rad-button-retry"))
            dialogResult = DialogResultEnum.retry;
        else if ($$target.hasClass("rad-button-abort"))
            dialogResult = DialogResultEnum.abort;
        else if ($$target.hasClass("rad-button-no"))
            dialogResult = DialogResultEnum.no;
        else if ($$target.hasClass("rad-button-yes"))
            dialogResult = DialogResultEnum.yes;
        else if ($$target.hasClass("rad-button-ok"))
            dialogResult = DialogResultEnum.ok;

        if (dialogResult)
        {
            self.dialogResult = dialogResult;
            await self.closeAsync();
        }
    }
    protected onKeyUp(evt: IJNativeEvent<KeyboardEvent>)
    {
        const
            self = evt.data.self as this,
            buttonStyle = self.buttonStyle.get();

        let
            buttonToClick;

        switch (evt.originalEvent.key)
        {
            case "Enter":
                {
                    switch (buttonStyle)
                    {
                        case ButtonStyleEnum.abortRetryIgnore:
                        case ButtonStyleEnum.retryCancel:
                            buttonToClick = self.buttonRetry;
                            break;
                        case ButtonStyleEnum.okCancel:
                        case ButtonStyleEnum.okOnly:
                            buttonToClick = self.buttonOk;
                            break;
                        case ButtonStyleEnum.yesNo:
                        case ButtonStyleEnum.yesNoCancel:
                            buttonToClick = self.buttonYes;
                            break;
                        case ButtonStyleEnum.cancelOnly:
                            break;
                    }
                    break;
                }
            case "Escape":
                {
                    switch (buttonStyle)
                    {
                        case ButtonStyleEnum.abortRetryIgnore:
                            buttonToClick = self.buttonAbort;
                            break;
                        case ButtonStyleEnum.retryCancel:
                        case ButtonStyleEnum.okCancel:
                        case ButtonStyleEnum.yesNoCancel:
                        case ButtonStyleEnum.cancelOnly:
                            buttonToClick = self.buttonCancel;
                            break;
                        case ButtonStyleEnum.yesNo:
                            buttonToClick = self.buttonNo;
                            break;
                        case ButtonStyleEnum.okOnly:
                            break;
                    }
                    break;
                }
        }

        if (buttonToClick)
        {
            evt.preventDefault();
            evt.stopPropagation();
            buttonToClick.click();
        }
    }

    // #endregion

    // #endregion
}
